import { useState, useRef } from 'react';
import { Upload, Download, RefreshCw, CheckCircle, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { csvService } from '../../services/api';
import { Button, Badge, Toast, Select } from '../../components/ui';
import { thStyle, tdStyle, iconBtnStyle } from '../../utils/tableStyles';
import { DUPLICATE_RULES, ROW_STATUS } from '../../constants';
import Papa from 'papaparse';

const CSVImportPage = () => {
  const { dark } = useTheme();
  const fileInputRef = useRef(null);

  const [duplicateRule, setDuplicateRule] = useState('mobile');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [hasValidated, setHasValidated] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState(null);

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const { data } = await csvService.getTemplate();
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = 'lead_import_template.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Failed to download template', type: 'error' });
    } finally { setDownloading(false); }
  };

  const runValidation = async (rowsToCheck, ruleOverride) => {
    if (!rowsToCheck.length) return;
    setValidating(true);
    try {
      const { data } = await csvService.validate({ rows: rowsToCheck.map(r => r.data), duplicateRule: ruleOverride || duplicateRule });
      const results = data.data.rows || [];
      setRows(rowsToCheck.map((r, i) => ({
        ...r,
        errors: results[i]?.errors || {},
        status: results[i]?.status || 'error',
        duplicateLeadId: results[i]?.duplicateLeadId || null,
      })));
      setHasValidated(true);
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Validation failed', type: 'error' });
    } finally { setValidating(false); }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    setParseError(''); setImportResult(null); setHasValidated(false); setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        if (result.errors?.length) setParseError(result.errors[0].message);
        setHeaders(result.meta.fields || []);
        const parsedRows = result.data.map(d => ({ data: d, errors: {}, status: 'unchecked', duplicateLeadId: null }));
        setRows(parsedRows);
        if (parsedRows.length) runValidation(parsedRows);
      },
      error: (err) => setParseError(err.message),
    });
  };

  const handleChangeFile = () => {
    setRows([]); setHeaders([]); setFileName(''); setParseError(''); setHasValidated(false); setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDuplicateRuleChange = (e) => {
    const val = e.target.value;
    setDuplicateRule(val);
    if (rows.length) runValidation(rows, val);
  };

  const getFieldError = (errors, header) => {
    if (!errors || typeof errors !== 'object') return '';
    if (errors[header]) return errors[header];
    const cleanHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');
    const matchingKey = Object.keys(errors).find(k => k.toLowerCase().replace(/[^a-z0-9]/g, '') === cleanHeader);
    return matchingKey ? errors[matchingKey] : '';
  };

  const handleCellChange = (rowIdx, field, value) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== rowIdx) return r;
      const cleanField = field.toLowerCase().replace(/[^a-z0-9]/g, '');
      const newErrors = { ...r.errors };
      delete newErrors[field];
      Object.keys(newErrors).forEach(k => {
        if (k.toLowerCase().replace(/[^a-z0-9]/g, '') === cleanField) {
          delete newErrors[k];
        }
      });
      return { ...r, data: { ...r.data, [field]: value }, errors: newErrors, status: 'unchecked' };
    }));
    setHasValidated(false);
  };

  const handleRemoveRow = (rowIdx) => setRows(prev => prev.filter((_, i) => i !== rowIdx));

  const handleImport = async () => {
    if (!rows.length) return;
    setImporting(true);
    try {
      const { data } = await csvService.importCSV({ rows: rows.map(r => r.data), duplicateRule });
      setToast({ msg: `Imported ${data.data.successCount} leads successfully`, type: 'success' });
      handleChangeFile();
      setImportResult(data.data);
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Import failed', type: 'error' });
    } finally { setImporting(false); }
  };

  const validCount = rows.filter(r => r.status === 'valid').length;
  const errorCount = rows.filter(r => r.status === 'error').length;
  const duplicateCount = rows.filter(r => r.status === 'duplicate').length;
  const uncheckedCount = rows.filter(r => r.status === 'unchecked').length;

  const cellStyle = (hasError) => ({
    width: 150, padding: '6px 8px', borderRadius: 6, fontSize: 12, outline: 'none', boxSizing: 'border-box',
    border: `1px solid ${hasError ? '#EF4444' : (dark ? '#334155' : '#E2E8F0')}`,
    background: hasError ? (dark ? '#EF44441A' : '#FEF2F2') : (dark ? '#0F172A' : '#F8FAFC'),
    color: dark ? '#F1F5F9' : '#0F172A',
  });

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>CSV Import</h1>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: '4px 0 0' }}>Bulk-upload leads from a CSV file</p>
        </div>
        <Button variant="outline" icon={Download} onClick={handleDownloadTemplate} disabled={downloading}>
          {downloading ? 'Downloading...' : 'Download Template'}
        </Button>
      </div>

      {rows.length === 0 ? (
        <div style={{ background: dark ? '#1E293B' : '#fff', borderRadius: 14, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, padding: 32, textAlign: 'center', marginBottom: 20 }}>
          <Upload size={40} color="#3B82F6" style={{ marginBottom: 14 }} />
          <h4 style={{ margin: '0 0 8px', color: dark ? '#F1F5F9' : '#0F172A' }}>Upload CSV File</h4>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: '0 0 20px' }}>
            <strong>fullName</strong> is always required — plus either <strong>mobile</strong> or <strong>email</strong>. All other columns are optional.
          </p>
          <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files?.[0])} />
          <div onClick={() => fileInputRef.current?.click()} style={{ border: `2px dashed ${dark ? '#475569' : '#D1D5DB'}`, borderRadius: 12, padding: 32, marginBottom: 8, cursor: 'pointer', maxWidth: 420, margin: '0 auto' }}>
            <p style={{ margin: 0, fontSize: 13, color: dark ? '#64748B' : '#94A3B8' }}>Click to select a CSV file</p>
          </div>
          {parseError && <div style={{ fontSize: 12, color: '#EF4444', marginTop: 8 }}>{parseError}</div>}
        </div>
      ) : (
        <>
          <div style={{ background: dark ? '#1E293B' : '#fff', borderRadius: 14, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, padding: '16px 20px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Upload size={18} color="#3B82F6" />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: dark ? '#F1F5F9' : '#0F172A' }}>{fileName}</div>
                <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>{rows.length} row{rows.length === 1 ? '' : 's'} parsed</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 200 }}>
                <Select label="Duplicate Rule" value={duplicateRule} onChange={handleDuplicateRuleChange} options={DUPLICATE_RULES} />
              </div>
              <Button variant="outline" size="sm" icon={RefreshCw} onClick={() => runValidation(rows)} disabled={validating}>
                {validating ? 'Validating...' : 'Re-validate'}
              </Button>
              <Button variant="ghost" size="sm" icon={X} onClick={handleChangeFile}>Change File</Button>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            {[
              { label: 'Total', value: rows.length, bg: dark ? '#1E293B' : '#fff', border: dark ? '#334155' : '#E2E8F0', color: dark ? '#F1F5F9' : '#0F172A' },
              { label: 'Valid', value: validCount, bg: dark ? '#10B9811A' : '#ECFDF5', border: '#10B98140', color: '#10B981' },
              { label: 'Errors', value: errorCount, bg: dark ? '#EF44441A' : '#FEF2F2', border: '#EF444440', color: '#EF4444' },
              { label: 'Duplicates', value: duplicateCount, bg: dark ? '#F59E0B1A' : '#FFFBEB', border: '#F59E0B40', color: '#F59E0B' },
            ].map(stat => (
              <div key={stat.label} style={{ padding: '8px 16px', borderRadius: 10, background: stat.bg, border: `1px solid ${stat.border}` }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: stat.color }}>{stat.value}</span>
                <span style={{ fontSize: 11, color: dark ? '#94A3B8' : '#64748B', marginLeft: 6 }}>{stat.label}</span>
              </div>
            ))}
            {uncheckedCount > 0 && (
              <div style={{ padding: '8px 16px', borderRadius: 10, background: dark ? '#334155' : '#F1F5F9', border: `1px solid ${dark ? '#475569' : '#E2E8F0'}` }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: dark ? '#94A3B8' : '#64748B' }}>{uncheckedCount}</span>
                <span style={{ fontSize: 11, color: dark ? '#94A3B8' : '#64748B', marginLeft: 6 }}>Needs re-validation</span>
              </div>
            )}
          </div>

          <div style={{ background: dark ? '#1E293B' : '#fff', borderRadius: 14, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, overflow: 'hidden', marginBottom: 16 }}>
            <div style={{ overflow: 'auto', maxHeight: 480 }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 12, width: 'max-content', minWidth: '100%' }}>
                <thead>
                  <tr style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                    <th style={{ ...thStyle(dark), position: 'sticky', left: 0, background: dark ? '#0F172A' : '#F8FAFC', zIndex: 3 }}>#</th>
                    <th style={{ ...thStyle(dark), position: 'sticky', left: 34, background: dark ? '#0F172A' : '#F8FAFC', zIndex: 3 }}>Status</th>
                    {headers.map(h => <th key={h} style={{ ...thStyle(dark), background: dark ? '#0F172A' : '#F8FAFC', zIndex: 1 }}>{h}</th>)}
                    <th style={{ ...thStyle(dark), background: dark ? '#0F172A' : '#F8FAFC', zIndex: 1 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const statusMeta = ROW_STATUS[r.status] || ROW_STATUS.unchecked;
                    return (
                      <tr key={idx} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ ...tdStyle(dark), position: 'sticky', left: 0, background: dark ? '#1E293B' : '#fff', verticalAlign: 'top' }}>{idx + 1}</td>
                        <td style={{ ...tdStyle(dark), position: 'sticky', left: 34, background: dark ? '#1E293B' : '#fff', verticalAlign: 'top' }}>
                          <Badge color={statusMeta.color}>{statusMeta.label}</Badge>
                          {r.status === 'duplicate' && r.duplicateLeadId && (
                            <div style={{ fontSize: 10, color: dark ? '#64748B' : '#94A3B8', marginTop: 2 }}>Matches {r.duplicateLeadId}</div>
                          )}
                          {r.status === 'error' && r.errors?.general && (
                            <div style={{ fontSize: 11, color: '#EF4444', marginTop: 2 }}>{r.errors.general}</div>
                          )}
                        </td>
                        {headers.map(h => {
                          const errMsg = getFieldError(r.errors, h);
                          return (
                            <td key={h} style={{ padding: '6px 6px', verticalAlign: 'top' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <input
                                  value={r.data[h] || ''}
                                  onChange={e => handleCellChange(idx, h, e.target.value)}
                                  title={errMsg || ''}
                                  style={cellStyle(!!errMsg)}
                                />
                                {errMsg && (
                                  <span style={{ fontSize: 11, color: '#EF4444', fontWeight: 500, lineHeight: 1.2, maxWidth: 150, wordBreak: 'break-word' }}>
                                    {errMsg}
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td style={{ ...tdStyle(dark), verticalAlign: 'top' }}>
                          <button onClick={() => handleRemoveRow(idx)} title="Remove row" style={iconBtnStyle(dark)}><X size={13} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 24 }}>
            <Button icon={CheckCircle} disabled={!hasValidated || validCount === 0 || importing} onClick={handleImport}>
              {importing ? 'Importing...' : `Import ${validCount} Valid Lead${validCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        </>
      )}

      {importResult && (
        <div style={{ marginBottom: 24, padding: 14, borderRadius: 10, background: dark ? '#10B9811A' : '#ECFDF5', border: '1px solid #10B98140', fontSize: 13, color: dark ? '#F1F5F9' : '#0F172A' }}>
          Import complete — {importResult.successCount} succeeded, {importResult.failedCount} failed, {importResult.duplicateCount} duplicates skipped.
        </div>
      )}
    </div>
  );
};

export default CSVImportPage;
